import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Brain, Trash2, Sparkles, Plus,
  CheckCircle2, AlertTriangle, User2, GraduationCap, Calendar, FileText,
} from 'lucide-react';
import { getAssessment, deleteAssessment } from '../../lib/assessments/storage';
import { MOCA } from '../../lib/assessments/moca';

function fmtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short' });
}

export default function AssessmentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(undefined); // undefined = loading

  useEffect(() => {
    setRecord(getAssessment(id) || null);
  }, [id]);

  if (record === undefined) {
    return (
      <main className="flex-1 bg-primary flex items-center justify-center">
        <div className="text-[13px] text-muted-c">Loading assessment…</div>
      </main>
    );
  }

  if (!record) {
    return (
      <main className="flex-1 bg-primary">
        <div className="max-w-[720px] mx-auto px-6 py-10 text-center">
          <Brain size={26} className="text-muted-c mx-auto mb-3" />
          <div className="text-[16px] font-bold text-primary-c mb-1">Assessment not found</div>
          <p className="text-[13px] text-muted-c mb-5">
            It may have been deleted, or this link refers to a record stored on another device.
          </p>
          <Link
            to="/app/tbi-assessments"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-[10px] text-[12px] font-semibold text-accent-light bg-accent-glow border border-strong hover:bg-hover-surface transition-all"
          >
            <ArrowLeft size={13} strokeWidth={2.5} /> Back to assessments
          </Link>
        </div>
      </main>
    );
  }

  const r = record.result;
  const ok = r?.isNormal;

  const handleDelete = () => {
    if (!window.confirm('Delete this assessment? This cannot be undone.')) return;
    deleteAssessment(record.id);
    navigate('/app/tbi-assessments', { replace: true });
  };

  return (
    <main className="flex-1 bg-primary">
      <div className="max-w-[960px] mx-auto px-6 py-8">
        {/* Back chip */}
        <Link
          to="/app/tbi-assessments"
          className="inline-flex items-center gap-1.5 text-[12px] text-muted-c hover:text-primary-c mb-4 transition-all"
        >
          <ArrowLeft size={13} strokeWidth={2.5} /> All assessments
        </Link>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={13} strokeWidth={2.2} className="text-accent-light" />
              <span className="text-[11px] uppercase tracking-wide text-accent-light font-semibold">
                {record.type === 'moca' ? `MoCA · v${MOCA.version}` : record.type}
              </span>
            </div>
            <h1 className="text-[24px] font-bold text-primary-c truncate">
              {record.patient_name}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDelete}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[10px] text-[12px] font-semibold text-danger border border-danger/30 hover:bg-danger/10 transition-all"
            >
              <Trash2 size={13} strokeWidth={2.2} /> Delete
            </button>
            <button
              onClick={() => navigate('/app/tbi-assessments/moca/new')}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow hover:opacity-95 transition-all"
            >
              <Plus size={13} strokeWidth={2.5} /> New assessment
            </button>
          </div>
        </div>

        {/* Score hero */}
        <div
          className={`rounded-[16px] border overflow-hidden mb-6 ${
            ok ? 'border-success/30 bg-success-soft' : 'border-warning/30 bg-warning/10'
          }`}
        >
          <div className="p-6 flex items-end gap-6 flex-wrap">
            <div className="flex items-baseline gap-1.5">
              <div
                className={`font-bold leading-none ${ok ? 'text-success' : 'text-warning'}`}
                style={{ fontSize: '72px', fontFamily: '"Bricolage Grotesque", sans-serif', letterSpacing: '-0.04em' }}
              >
                {r?.total ?? '—'}
              </div>
              <div className={`text-[28px] font-light ${ok ? 'text-success' : 'text-warning'} opacity-70`}>
                / {r?.max ?? 30}
              </div>
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2 mb-1">
                {ok
                  ? <CheckCircle2 size={15} className="text-success" strokeWidth={2.2} />
                  : <AlertTriangle size={15} className="text-warning" strokeWidth={2.2} />
                }
                <span className={`text-[13px] font-bold ${ok ? 'text-success' : 'text-warning'}`}>
                  {ok ? 'Within normal range' : 'Below normal threshold'}
                </span>
              </div>
              <div className="text-[11px] text-muted-c leading-relaxed">
                Normal ≥ {r?.normalThreshold ?? 26} / 30 ·{' '}
                {r?.bonusApplied ? '+1 education bonus applied · ' : ''}
                <span className="opacity-80">Screening only — not a diagnosis.</span>
              </div>
            </div>
          </div>
        </div>

        {/* Patient meta */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { Icon: User2, label: 'Patient', value: record.patient_name },
            { Icon: GraduationCap, label: 'Education', value: record.education_years != null ? `${record.education_years} yrs` : '—' },
            { Icon: Calendar, label: 'Completed', value: fmtDateTime(record.completed_at) },
            { Icon: Brain, label: 'Assessment', value: `MoCA v${MOCA.version}` },
          ].map(m => (
            <div key={m.label} className="rounded-[12px] border border-theme bg-elevated p-3">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide font-semibold text-muted-c mb-1.5">
                <m.Icon size={11} strokeWidth={2.2} /> {m.label}
              </div>
              <div className="text-[13px] text-primary-c font-medium truncate">{m.value}</div>
            </div>
          ))}
        </div>

        {/* Per-section breakdown */}
        <section className="mb-6">
          <div className="text-[11px] uppercase tracking-wide text-muted-c font-semibold mb-3">
            Section breakdown
          </div>
          <div className="rounded-[14px] border border-theme bg-elevated overflow-hidden divide-y divide-theme">
            {r && Object.entries(r.bySection)
              .filter(([, s]) => s.max > 0)
              .map(([sid, s]) => {
                const pct = s.max > 0 ? (s.score / s.max) * 100 : 0;
                const full = s.score === s.max;
                return (
                  <div key={sid} className="px-5 py-3.5 grid grid-cols-[180px_1fr_70px] items-center gap-4">
                    <div className="text-[13px] font-semibold text-primary-c">{s.title}</div>
                    <div className="h-1.5 rounded-full bg-tertiary overflow-hidden">
                      <div
                        className={`h-full rounded-full ${full ? 'bg-success' : 'bg-accent-light'}`}
                        style={{ width: `${pct}%`, transition: 'width 0.4s ease' }}
                      />
                    </div>
                    <div className="text-right font-mono text-[12px] font-bold text-primary-c">
                      {s.score} <span className="text-muted-c font-normal">/ {s.max}</span>
                    </div>
                  </div>
                );
              })}
          </div>
        </section>

        {/* Clinical note */}
        {record.patient_notes && (
          <section className="mb-6">
            <div className="text-[11px] uppercase tracking-wide text-muted-c font-semibold mb-2 flex items-center gap-1.5">
              <FileText size={11} strokeWidth={2.2} /> Clinical note
            </div>
            <div className="rounded-[12px] border border-theme bg-elevated p-4 text-[13px] text-secondary-c leading-relaxed whitespace-pre-wrap">
              {record.patient_notes}
            </div>
          </section>
        )}

        {/* Footnote */}
        <div className="mt-8 flex items-start gap-2 text-[11px] text-muted-c max-w-[680px]">
          <AlertTriangle size={12} strokeWidth={2} className="mt-0.5 flex-shrink-0" />
          <p>
            MoCA is © Z. Nasreddine, MD (mocatest.org). Clinical interpretation
            requires a trained provider. This screen is not a diagnosis on its own.
          </p>
        </div>
      </div>
    </main>
  );
}
